import { useEffect, useMemo, useRef, useState } from "react";
import ScrollArea from "../ScrollArea.jsx";
import PageHero from "../PageHero.jsx";
import TraceTable from "../TraceTable.jsx";
import { KernelBlock } from "../kernelPrimitives/index.jsx";
import { DEFAULT_LAYER_COLORS } from "../kernelPrimitives/shared.jsx";
import { NOTE_CLASS } from "../../lib/ui.js";
import { query, useQuery } from "../../lib/dataClient.js";
import { bucketFiringByCommand, BUCKETS } from "../../lib/signalStore.js";
import { groupByName } from "../skills/grouping.js";
import { emptyBodyWindow, pendingBodyIds, unresolvedBodyIds, startFetch, resolveFetch } from "./skillBodyWindow.js";

// Trace-list page size — the initial window, and the step each "Load more" adds.
// (Why the window drives the chips + bodies: see the component docblock below.)
const TRACE_LIMIT = 200;
const normalize = (s) => String(s || "").replace(/\s+/g, " ").trim();

function FilterChip({ label, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "rounded-[2px] border px-[6px] py-[2px] text-[10px] leading-[1.2] tracking-[0.04em] uppercase transition-colors cursor-pointer",
        active
          ? "border-[color:var(--green)] bg-[color:var(--green-soft)] text-[color:var(--ink)]"
          : "border-[color:var(--line)] text-[color:var(--muted)] hover:text-[color:var(--ink)] hover:border-[color:var(--line-strong)]",
      ].join(" ")}
    >
      {label}
    </button>
  );
}

// Per-skill VALUE lens — "content-compare". List-first: it loads the skill's
// scoped prompts (newest-first), reads each in-window prompt's injected SKILL.md
// body, and groups distinct bodies into versions (>1 = drift). The prompts render
// as a trace-log-style TraceTable; the versions are filter chips derived from the
// same window — clicking one filters the list to that version AND opens its body
// in the right detail panel.
export default function SkillDetailBody({ nav, slot, ctx }) {
  const scopePath = nav?.scopePath || [];
  const terminal = scopePath.length ? scopePath[scopePath.length - 1] : null;
  const skillName = terminal?.dim === "skills" ? (terminal.ids?.[0] ?? terminal.id ?? null) : null;

  // Identity of the current read — reset the paged window and the accumulated
  // bodies whenever the skill / scope / range changes, so a new target never
  // inherits the previous one's loaded rows.
  const queryKey = useMemo(
    () => JSON.stringify([skillName, scopePath, ctx?.range ?? null]),
    [skillName, scopePath, ctx?.range],
  );

  // The skill's scoped prompts — the trace list, and the SOURCE OF TRUTH for
  // this view. Newest-first; the window grows by TRACE_LIMIT each "load more"
  // (see the footer), which widens the drift picture since the version chips are
  // derived from exactly these rows — a chip can never advertise a version the
  // list isn't showing, and loading older prompts is how older versions appear.
  const [rowLimit, setRowLimit] = useState(TRACE_LIMIT);
  const { data: traces = [], loading: tracesLoading } = useQuery(
    "getTraces",
    { scopePath, range: ctx?.range, limit: rowLimit, sort: "newest" },
    { enabled: Boolean(skillName) },
  );
  const windowIds = useMemo(() => traces.map((t) => t.id), [traces]);

  // READ each prompt's injected SKILL.md body from the backend `skill`
  // signal_tags (value = the full body). Paging is INCREMENTAL: each grow of the
  // window fetches bodies only for its NEW event_ids and accumulates them. So we
  // never re-download bodies already held — the same handful of distinct bodies
  // would otherwise be re-fetched, one copy per prompt, on every "load more" —
  // and each request stays bounded to a page (well under the analytics IN-list
  // and response-size limits). Versions are grouped from the accumulated set.
  // Same byte-portable skillExtractor kernel (parity-tested); the view reads the
  // materialized annotation.
  const [found, setFound] = useState([]); // accumulated { eventId, body } rows
  const [bodyLoading, setBodyLoading] = useState(false);
  // id-state machine (skillBodyWindow) held in a ref, NOT state: the fetch
  // effect mutates it without re-running (depending on it would loop — marking
  // in flight would re-fire the effect). `found` mirrors the resolved rows for
  // rendering. See skillBodyWindow.js for the fetched/inflight/release contract.
  const winRef = useRef(emptyBodyWindow());

  useEffect(() => {
    // Query identity changed — drop the accumulated window and start over.
    winRef.current = emptyBodyWindow();
    setFound([]);
    setRowLimit(TRACE_LIMIT);
  }, [queryKey]);

  useEffect(() => {
    if (!skillName) return undefined;
    const missing = pendingBodyIds(windowIds, winRef.current);
    if (!missing.length) return undefined;
    winRef.current = startFetch(winRef.current, missing);
    let alive = true;
    setBodyLoading(true);
    query("getSignalTagValues", { namespace: "skill", tag: skillName, scopePath, range: ctx?.range, eventIds: missing })
      .then((res) => {
        // Superseded (window/scope/skill changed) — the cleanup already released
        // these ids; don't graduate them or the next window won't retry.
        if (!alive) return;
        // getSignalTagValues swallows failures to { rows: [], ok: false } rather
        // than rejecting, so branch on `ok`: release a failed fetch (retry later),
        // graduate a real one (even if it carried no body) so it isn't re-requested.
        const ok = res?.ok !== false;
        winRef.current = resolveFetch(winRef.current, missing, ok);
        if (ok) {
          const rows = (res?.rows ?? [])
            .map((r) => ({ eventId: r.event_id, body: String(r.value ?? "") }))
            .filter((r) => r.body);
          if (rows.length) setFound((prev) => [...prev, ...rows]);
        }
      })
      // Defensive: an infra-level reject (before execute's own try/catch) — treat
      // as failure so the ids are released and retried.
      .catch(() => { winRef.current = resolveFetch(winRef.current, missing, false); })
      .finally(() => { if (alive) setBodyLoading(false); });
    return () => {
      // Superseded before this run resolved — release its ids (nothing reached
      // `fetched`, so this can't drop bodies) so the next window refetches them.
      alive = false;
      winRef.current = resolveFetch(winRef.current, missing, false);
    };
    // scopePath / range are captured through queryKey; depending on them
    // directly would re-fire on every render (fresh object identities).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowIds, queryKey, skillName]);

  const versions = useMemo(
    () => groupByName(found, (r) => normalize(r.body)).map((g, i) => ({
      n: i + 1,
      count: g.rows.length,
      body: g.rows[0].body,
      ids: new Set(g.rows.map((r) => r.eventId)),
    })),
    [found],
  );
  const [versionFilter, setVersionFilter] = useState(null);

  // A prompt can carry MORE THAN ONE version's body (it injected this skill more
  // than once with differing content — e.g. a line-numbered Read echo alongside
  // an injected copy), so it belongs to several versions at once. Each version
  // keeps its own `ids` set — NOT a single id→version map, which would keep only
  // the last version set and empty out every earlier one.
  const selectedVersion = useMemo(
    () => (versionFilter ? versions.find((v) => v.n === versionFilter) : null),
    [versions, versionFilter],
  );
  // Selecting a version filters the ALREADY-LOADED rows — no refetch — and the
  // ids are guaranteed present since `found` was hydrated from these same rows.
  const rows = useMemo(
    () => (selectedVersion ? traces.filter((t) => selectedVersion.ids.has(t.id)) : traces),
    [traces, selectedVersion],
  );

  // Activity surface: each version as an overlapping line over the window's time
  // span. A prompt in two versions contributes to both lines.
  const versionSeries = useMemo(() => {
    const asResults = new Map();
    for (const v of versions) {
      for (const id of v.ids) {
        const arr = asResults.get(id) ?? [];
        arr.push({ command: `v${v.n}` });
        asResults.set(id, arr);
      }
    }
    const byCmd = bucketFiringByCommand(traces, asResults);
    return versions.map((v, i) => ({
      label: `v${v.n}`,
      series: byCmd.get(`v${v.n}`) || new Array(BUCKETS).fill(0),
      color: DEFAULT_LAYER_COLORS[i % DEFAULT_LAYER_COLORS.length],
    }));
  }, [traces, versions]);

  function openVersion(v) {
    setVersionFilter(v.n);
    ctx?.onOpenDetail?.({
      eyebrow: skillName,
      title: `Version ${v.n} · ${v.count} prompt${v.count === 1 ? "" : "s"}`,
      body: v.body,
      detailKey: `${skillName}:v${v.n}`,
    });
    slot?.onExpandDetail?.();
  }
  const onSelectTrace = (id) => { ctx?.traceLog?.onSelectTrace?.(id); slot?.onExpandDetail?.(); };

  if (!skillName) {
    return (
      <ScrollArea data-content-scroll className="min-h-0 h-full" contentClassName="grid gap-[12px] pb-[18px]">
        <PageHero eyebrow="Skill" title="No skill selected" />
      </ScrollArea>
    );
  }

  // Distinct PROMPTS carrying a captured body — NOT found.length, which counts
  // body records (a prompt that injects the skill more than once contributes
  // several, and shows up in several version chips). Counting prompts keeps the
  // "prompts carrying the body" label honest.
  const bodyCount = new Set(found.map((r) => r.eventId)).size;
  // "reading" covers the gap between a grown window and its bodies arriving —
  // including the tick before the fetch effect marks the new ids — so the status
  // never flashes "no body" mid-page.
  const readingBodies = bodyLoading || unresolvedBodyIds(windowIds, winRef.current).length > 0;
  const status = readingBodies
    ? "reading skill bodies…"
    : bodyCount === 0
      ? "No injected body captured for this skill in scope yet."
      : `${bodyCount} prompt${bodyCount === 1 ? "" : "s"} carry an injected body · ${versions.length} version${versions.length === 1 ? "" : "s"}${versions.length > 1 ? " (drift)" : ""}`;

  // Load-more: the trace list is capped at rowLimit; getTraces reports the true
  // scope total. Growing rowLimit widens the window (and thus the versions).
  // Gate on "did the page fill to the cap" rather than length < total: the
  // deduped row count can sit a hair under count_distinct, which would otherwise
  // leave a "Load N more" that never clears.
  const scopeTotal = traces?.total ?? null;
  const hasMore = traces.length >= rowLimit && (scopeTotal == null || traces.length < scopeTotal);
  const listFooter = hasMore ? (
    <div className="flex justify-center px-[6px] py-[8px]">
      <button
        type="button"
        onClick={() => setRowLimit((l) => l + TRACE_LIMIT)}
        disabled={tracesLoading || bodyLoading}
        className="rounded-[2px] border border-[color:var(--line)] px-[12px] py-[5px] text-[11px] leading-[1.2] text-[color:var(--muted)] transition-colors hover:text-[color:var(--ink)] hover:border-[color:var(--line-strong)] disabled:opacity-50 disabled:cursor-default cursor-pointer"
      >
        {tracesLoading
          ? "Loading…"
          : scopeTotal != null
            ? `Load ${Math.min(TRACE_LIMIT, scopeTotal - traces.length)} more`
            : "Load more"}
        <span className="text-[color:var(--muted-soft)]">
          {scopeTotal != null ? ` · ${traces.length} of ${scopeTotal} loaded` : ` · ${traces.length} loaded`}
        </span>
      </button>
    </div>
  ) : null;

  return (
    <ScrollArea data-content-scroll className="min-h-0 h-full" contentClassName="grid gap-[12px] pb-[18px]">
      <PageHero
        eyebrow="Skill"
        title={skillName}
        detail="Prompts that injected this skill. Filter by version, then select a version to read it or a row to inspect the prompt."
        metrics={[
          { label: "With body", value: bodyCount ? String(bodyCount) : "–", note: "prompts carrying the body" },
          { label: "Versions", value: bodyCount ? String(versions.length) : "–", note: versions.length > 1 ? "definitions differ" : "stable" },
        ]}
      />
      <span className={NOTE_CLASS}>{status}</span>
      <KernelBlock spec={{ kind: "chart", mode: "line", layers: versionSeries, height: 130, range: ctx?.range, title: "Version distribution" }} />
      <TraceTable
        toolbar={
          versions.length ? (
            <div className="flex flex-wrap items-center gap-[4px]">
              <FilterChip label="All versions" active={!versionFilter} onClick={() => setVersionFilter(null)} />
              {versions.map((v) => (
                <FilterChip key={v.n} label={`v${v.n} · ${v.count}`} active={versionFilter === v.n} onClick={() => openVersion(v)} />
              ))}
            </div>
          ) : null
        }
        rows={rows}
        footer={listFooter}
        selectedTraceId={ctx?.traceLog?.selectedTraceId || ""}
        onSelectTrace={onSelectTrace}
        showEndpoint={ctx?.traceLog?.showEndpoint}
        loading={tracesLoading}
        emptyMessage={bodyLoading ? "Reading…" : "No prompts carry an injected body for this skill in scope."}
      />
    </ScrollArea>
  );
}
